/**
 * entitlements — plan feature catalogue (display/UX only).
 *
 * The DATABASE is authoritative: `has_entitlement` / `require_entitlement`
 * (migration 00139) gate features server-side. This module is the pure, shared
 * source for feature KEYS + human labels used by the UI (Plan tab, gates).
 * Keys mirror the `subscription_plans.entitlements` JSONB + the ai/bank_feed
 * columns. Stable strings — never plan names — so re-pricing never touches gates.
 *
 * @since 1.0.0
 */

export const FEATURE_LABELS: Record<string, string> = {
    ai: 'AI agents & cockpit',
    bank_feed: 'Bank feeds',
    estimates: 'Estimates & quotations',
    credit_debit_notes: 'Credit & debit notes',
    recurring: 'Recurring invoices & bills',
    payment_links: 'Payment links',
    inventory: 'Inventory',
    bank_reconciliation: 'Bank reconciliation',
    manufacturing: 'Manufacturing',
    warehouse: 'Warehouse operations',
    qms: 'Quality management',
    projects: 'Projects',
    fixed_assets: 'Fixed assets',
    multi_currency: 'Multi-currency',
    report_builder: 'Custom report builder',
    budgeting: 'Budgeting',
    approvals: 'Approvals',
    api: 'API & webhooks',
    consolidation: 'Multi-entity consolidation',
    multi_entity: 'Multi-entity',
    practice_dashboard: 'Practice dashboard',
    client_portal: 'Client portal',
    roc_journey: 'ROC Journey',
    lab: 'Karr Lab (LIS)',
    travel: 'Karr Travel',
    // mod-5 — the module SKU rail. Labels for module-activation entitlement keys
    // so a plan CAN gate them as add-on SKUs. Until a plan lists the key it is
    // ALLOWED (see featureAllowed), so these are inert without the founder's
    // pricing migration. The POS per-outlet SKU lands on `pos`.
    pos: 'Point of Sale',
    restaurant: 'Restaurant',
    orders: 'Sales Orders & RFQ',
    crm: 'CRM',
    helpdesk: 'Helpdesk',
    subscriptions: 'Subscription Billing',
    maintenance: 'Asset Maintenance',
    ecommerce: 'Selling platforms',
    projections: 'Projections & Bank Finance',
    // pp-2 — the two book-surface keys the practice classes divide the core spine
    // on (`src/shared/pricing/practiceClassCapability.ts`). No plan lists them and
    // nothing calls require_entitlement on them yet, so they are inert in both
    // directions; pp-6 seeds them onto the retail plans.
    books: 'Live books & posting',
    finalization: 'Finalization & audit workbench',
};

export type FeatureKey = keyof typeof FEATURE_LABELS;

/** Normalise an optional `feature:` prefix to the bare JSONB key. */
export function featureKey(key: string): string {
    return key.replace(/^feature:/, '');
}

export function featureLabel(key: string): string {
    const k = featureKey(key);
    return FEATURE_LABELS[k] ?? k;
}

/**
 * Missing key ⇒ allowed (docs/MODULES.md Sec. 4). A feature is only WITHHELD when the
 * org's plan explicitly lists it as `false`; a key that no plan mentions is not a
 * gate yet, so it's allowed. This is what keeps the module SKU rail inert until
 * the founder's optional pricing migration adds the keys to plans (mod-5).
 *
 * `features` is the map from `my_entitlements` (the current plan's `entitlements`
 * JSONB + ai/bank_feed) — a key absent from it is absent from that plan.
 */
export function featureAllowed(key: string, features: Record<string, boolean>): boolean {
    const k = featureKey(key);
    return !(k in features) || features[k] === true;
}

/** Display name for a plan — the seeded "Free Forever" is now a time-boxed trial
 *  (30 days since rp-7; the length lives in `retail_price_card.trial_days`), so show "Free". */
export function displayPlanName(name: string | null | undefined): string {
    if (!name) return '';
    return name === 'Free Forever' ? 'Free' : name;
}
